import type {
  InjuryStatuses,
  OddsRequest,
  OddsResponse,
  PublishSimulationRequest,
  RequestSimulationRunRequest,
  RequestSimulationRunResponse,
  SimulationRun,
  SimulationRunsResponse,
  SimulationStatus
} from '@library/types/simulations';
import { DateTime } from 'luxon';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { ProjectionsAndScoresAPIResponse } from '@library/types/projection';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { organizeExistingProjectionsByMarket } from '@bo-utils/simulations';

const { admin } = apiPaths;

export const simulationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getExistingProjections: build.query<
      {
        existingProjections: Array<GamePlayerProjectionTrading>;
      },
      {
        allMarkets: Array<{
          league_id: string;
          market: string;
          market_id: string;
          stat_type_id: number;
        }>;
        gameId: string;
        leagueId: string;
      }
    >({
      query: ({ gameId = undefined, leagueId }) => ({
        url: `${admin}projections`,
        method: 'GET',
        params: {
          from_date: DateTime.now().minus({ days: 1 }).toISODate(),
          game_ids: gameId,
          league_id: leagueId,
          page: 1,
          per_page: 10000
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (
        response: ProjectionsAndScoresAPIResponse,
        _,
        { allMarkets }
      ) => {
        return organizeExistingProjectionsByMarket({
          allMarkets,
          projections: response
        });
      }
    }),
    getSimulations: build.query<
      SimulationRunsResponse,
      {
        gameId: string;
        leagueId: string;
        simulationRunType?: string;
        simulationRunStatus?: SimulationStatus;
        simulationUserId?: string;
        publicationUserId?: string;
      }
    >({
      query: ({
        gameId,
        leagueId,
        publicationUserId,
        simulationRunStatus,
        simulationRunType,
        simulationUserId
      }) => ({
        url: `${admin}games/${gameId}/simulation_runs`,
        method: 'GET',
        params: {
          league_id: leagueId,
          publication_user_id: publicationUserId,
          simulation_run_status: simulationRunStatus,
          simulation_run_type: simulationRunType,
          simulation_user_id: simulationUserId
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      })
    }),
    getSimulation: build.query<
      SimulationRun,
      {
        gameId: string;
        leagueId: string;
        simulationId: string;
      }
    >({
      query: ({ gameId, leagueId, simulationId }) => ({
        url: `${admin}games/${gameId}/simulation_runs/${simulationId}`,
        method: 'GET',
        params: {
          league_id: leagueId
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      providesTags: (_result, _error, { simulationId }) => [
        { type: 'Simulation', id: simulationId }
      ]
    }),
    requestSimulationRun: build.mutation<
      RequestSimulationRunResponse,
      RequestSimulationRunRequest
    >({
      query: (payload) => ({
        url: `${admin}games/${payload.game_id}/simulation_runs`,
        method: 'POST',
        body: { simulation_run: payload },
        params: { league_id: payload.league_id },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      })
    }),
    getSimulationOdds: build.query<OddsResponse, OddsRequest>({
      query: ({
        gameId,
        leagueId,
        market,
        prizepicksPlayerId,
        prizepicksTeamId,
        simulationId,
        simulationRunType,
        sportradarPlayerId,
        sportradarTeamId
      }) => ({
        url: `${admin}games/${gameId}/simulation_runs/${simulationId}/odds`,
        method: 'GET',
        params: {
          league_id: leagueId,
          market,
          prizepicks_player_id: prizepicksPlayerId,
          prizepicks_team_id: prizepicksTeamId,
          simulation_run_type: simulationRunType,
          sportradar_player_id: sportradarPlayerId,
          sportradar_team_id: sportradarTeamId
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      })
    }),
    publishSimulation: build.mutation<unknown, PublishSimulationRequest>({
      query: ({
        gameId,
        leagueId,
        pricingStrategy,
        projections_request: payload,
        simulationId
      }) => ({
        url: `${admin}games/${gameId}/simulation_runs/${simulationId}/publish`,
        method: 'POST',
        params: {
          league_id: leagueId,
          pricing_strategy: pricingStrategy
        },
        body: { projections_request: payload },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      invalidatesTags: (_result, _error, { simulationId }) => [
        { type: 'Simulation', id: simulationId }
      ],
      transformErrorResponse: (response) => response.data
    }),
    getPlayerNews: build.query<
      {
        articles: Array<{
          analysis: string;
          headline: string;
          id: string;
          injury: {
            status: InjuryStatuses;
            type: string;
            location: string;
            details: string;
            side: string;
            return_date: string;
            sources: Array<string>;
          } | null;
          is_transaction: boolean;
          notes: string;
          player: {
            first_name: string;
            id: string;
            last_name: string;
            platoon: string;
            position: string;
            team_id: string;
          };
          posted_at: string;
          priority: number;
        }>;
      },
      {
        league_id: string;
        from_date: string;
        to_date?: string;
        player_id?: string;
        team?: string;
      }
    >({
      query: ({ league_id, from_date, to_date, player_id, team }) => ({
        url: `${admin}player_news`,
        method: 'GET',
        params: {
          league_id,
          from_date,
          to_date,
          player_id,
          team
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'News', id: `${arg.team}-${arg.from_date}` }
      ]
    }),
    getPlayerInjuries: build.query<
      {
        updated_at: string;
        players: Array<{
          player: {
            id: string;
            first_name: string;
            last_name: string;
            platoon: string;
            team_id: string;
          };
          injuries: Array<{
            id: string;
            status: InjuryStatuses;
            type: string;
            location: string;
            details: string;
            side: string;
            return_date: string;
            sources: Array<string>;
            created_at: string;
            updated_at: string;
          }>;
        }>;
      },
      {
        league_id: string;
        player_id?: string;
        team?: string;
        platoon?: string;
      }
    >({
      query: (payload) => ({
        url: `${admin}player_injuries`,
        method: 'GET',
        params: {
          league_id: payload.league_id,
          player_id: payload.player_id,
          team: payload.team,
          platoon: payload.platoon
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      })
    }),
    getMarkets: build.query<
      {
        markets: Array<{
          market_id: string;
          market: string;
          stat_type_id: number;
          league_id: string;
        }>;
      },
      {
        league_id: string;
      }
    >({
      query: (payload) => ({
        url: `${admin}leagues/${payload.league_id}/simulations/stat_types`,
        method: 'GET',
        params: {
          league_id: payload.league_id
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      })
    })
  })
});

export const {
  useGetExistingProjectionsQuery,
  useGetMarketsQuery,
  useGetPlayerNewsQuery,
  useGetPlayerInjuriesQuery,
  useGetSimulationOddsQuery,
  useGetSimulationQuery,
  useGetSimulationsQuery,
  usePublishSimulationMutation,
  useRequestSimulationRunMutation
} = simulationsApi;
